import { Link } from 'react-router-dom';
import PageTemplate from '../../../components/feature/PageTemplate';
import SharedProcessSection from '../../../components/feature/SharedProcessSection';
import SharedFAQSection from '../../../components/feature/SharedFAQSection';

function Hero() {
  return (
    <section className="relative min-h-[70vh] flex flex-col justify-end overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/39a8a03a2fa14aab4d3b81f4e8c5267b.jpg"
          alt="Commercial roofing Utah"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/50 to-charcoal/20"></div>
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 pb-16 pt-36 w-full">
        <div className="flex items-center gap-2 text-white/50 font-body text-xs mb-4">
          <Link to="/" className="hover:text-white cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-white/80">Commercial Roofing</span>
        </div>
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-brand-orange text-white text-xs font-700 uppercase tracking-widest px-4 py-1.5 rounded-full mb-5 font-body">
            <i className="ri-building-4-fill"></i> All Building Types
          </div>
          <h1 className="font-heading font-900 text-white text-5xl md:text-7xl lg:text-8xl uppercase tracking-tight leading-none mb-4">
            Commercial Roofing<br />
            <span className="text-brand-orange">Utah Businesses Trust</span>
          </h1>
          <p className="font-body text-white/70 text-lg mb-8 max-w-2xl leading-relaxed">
            Premium commercial roofing systems for warehouses, office buildings, retail centers, and multi-unit properties — on schedule, on budget, code-compliant.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/#estimate" className="flex items-center gap-2 bg-brand-orange hover:bg-brand-orange-dark text-white font-heading font-800 text-base uppercase tracking-widest px-8 py-3.5 rounded-md cursor-pointer whitespace-nowrap transition-colors">
              <i className="ri-calendar-check-fill"></i> Request Commercial Quote
            </Link>
            <a href="tel:+18015550199" className="flex items-center gap-2 border border-white/30 hover:border-white text-white font-heading font-800 text-base uppercase tracking-widest px-8 py-3.5 rounded-md cursor-pointer whitespace-nowrap transition-colors">
              <i className="ri-phone-fill text-brand-orange"></i> (801) 555-0199
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function MainSection() {
  const SYSTEMS = [
    { icon: 'ri-building-4-fill', title: 'TPO Roofing', subtitle: 'Best Value', desc: 'Thermoplastic single-ply membranes with heat-welded seams. Excellent UV and chemical resistance — ideal for Utah\'s high-altitude sun exposure.' },
    { icon: 'ri-layers-fill', title: 'EPDM Roofing', subtitle: '30+ Year Lifespan', desc: 'Durable rubber roofing with outstanding weathering resistance for commercial flat roofs. Proven performance in Utah\'s freeze-thaw climate cycles.' },
    { icon: 'ri-contrast-2-fill', title: 'Metal Roofing', subtitle: 'Maximum Longevity', desc: 'Standing seam and corrugated metal for commercial applications — superior for Utah snow loads with minimal maintenance requirements.' },
    { icon: 'ri-stack-fill', title: 'Modified Bitumen', subtitle: 'Multi-Layer Protection', desc: 'Multi-ply bitumen systems for high-traffic commercial roofs. Enhanced puncture resistance and redundant waterproofing layers.' },
    { icon: 'ri-bar-chart-fill', title: 'Spray Foam (SPF)', subtitle: 'Seamless & Insulating', desc: 'Spray polyurethane foam provides seamless waterproofing with exceptional R-value for Utah commercial energy code compliance.' },
    { icon: 'ri-refresh-fill', title: 'Roof Restoration', subtitle: 'Extend Roof Life', desc: 'Coating systems that extend existing commercial roof life by 10–15 years — a cost-effective alternative to full replacement.' },
  ];

  return (
    <section className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <div className="inline-block w-16 h-1 bg-brand-orange rounded-full mb-6"></div>
          <h2 className="font-heading font-900 text-charcoal text-5xl md:text-6xl uppercase tracking-tight leading-none mb-4">
            Commercial Roofing <span className="text-brand-orange">Systems</span>
          </h2>
          <p className="font-body text-charcoal/60 text-lg max-w-xl mx-auto">Every commercial system is selected for your building type, use case, and Utah climate performance.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-14">
          {SYSTEMS.map((s) => (
            <div key={s.title} className="bg-surface hover:bg-surface-dark transition-colors rounded-2xl p-7">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 flex items-center justify-center bg-brand-orange/10 rounded-lg flex-shrink-0">
                  <i className={`${s.icon} text-brand-orange text-2xl`}></i>
                </div>
                <div>
                  <p className="font-body text-brand-orange text-xs font-700 uppercase tracking-widest">{s.subtitle}</p>
                  <h3 className="font-heading font-900 text-charcoal text-xl uppercase tracking-wide">{s.title}</h3>
                </div>
              </div>
              <p className="font-body text-charcoal/60 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
        {/* Stats */}
        <div className="bg-charcoal rounded-2xl p-8 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[['500+', 'Commercial Projects'], ['15+', 'Years Experience'], ['100%', 'Licensed & Insured'], ['5yr+', 'Warranty Options']].map(([num, label]) => (
            <div key={label}>
              <div className="font-heading font-900 text-brand-orange text-4xl mb-1">{num}</div>
              <div className="font-body text-white/50 text-xs uppercase tracking-widest">{label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AIBlock() {
  return (
    <section className="py-28 bg-charcoal">
      <div className="max-w-5xl mx-auto px-6">
        <div className="bg-charcoal-light rounded-2xl p-10 border-l-4 border-brand-orange">
          <h2 className="font-heading font-900 text-white text-4xl md:text-5xl uppercase tracking-tight mb-6">
            Commercial Roofing <span className="text-brand-orange">in Utah</span>
          </h2>
          <p className="font-body text-white/70 text-lg leading-relaxed mb-8">
            <strong className="text-white">IWC Roofing Utah</strong> provides full-scope commercial roofing for Utah businesses — from small retail buildings to large warehouse complexes. Our commercial division specializes in flat and low-slope systems engineered to handle Utah&apos;s dramatic temperature swings, heavy snow loads, and intense UV exposure.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: 'Building Types Served', icon: 'ri-building-4-fill', items: ['Warehouses & industrial', 'Office buildings', 'Retail strip centers', 'Multi-family apartment', 'Churches & schools', 'HOA common areas'] },
              { title: 'Commercial Services', icon: 'ri-tools-fill', items: ['New construction roofing', 'Full roof replacement', 'Preventative maintenance', 'Emergency repairs', 'Roof restoration coatings', 'Annual inspection programs'] },
              { title: 'Why Commercial Clients Choose IWC', icon: 'ri-star-fill', items: ['On-time project delivery', 'Minimal business disruption', 'Detailed progress reports', 'Code & permit management', 'Safety-compliant crews', 'Post-project warranty support'] },
            ].map((col) => (
              <div key={col.title}>
                <div className="flex items-center gap-2 mb-3">
                  <i className={`${col.icon} text-brand-orange text-xl`}></i>
                  <h3 className="font-heading font-800 text-brand-orange text-sm uppercase tracking-widest">{col.title}</h3>
                </div>
                <ul className="space-y-2">
                  {col.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 font-body text-white/60 text-sm">
                      <i className="ri-arrow-right-s-fill text-brand-orange flex-shrink-0 mt-0.5"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const PROCESS = [
  { icon: 'ri-search-eye-line', title: 'Site Assessment', desc: 'Comprehensive evaluation of your building structure, current system, drainage, and project requirements.' },
  { icon: 'ri-file-text-line', title: 'Scope & Proposal', desc: 'Detailed commercial proposal including system specification, phasing plan, and fixed-price contract.' },
  { icon: 'ri-file-shield-2-line', title: 'Permits & Prep', desc: 'We handle all required permits, safety planning, and material procurement before work begins.' },
  { icon: 'ri-hammer-line', title: 'Installation', desc: 'Certified commercial crews install your system with minimal disruption to your business operations.' },
  { icon: 'ri-checkbox-circle-line', title: 'Warranty & Handoff', desc: 'Final inspection, warranty registration, and maintenance program setup for your new commercial roof.' },
];

const FAQS = [
  { q: 'What commercial roofing systems does IWC Roofing install?', a: 'IWC Roofing Utah installs all major commercial roofing systems including TPO, EPDM, modified bitumen, standing seam metal, spray polyurethane foam, and restoration coating systems. We recommend the ideal system based on your building type, budget, and performance requirements in Utah\'s climate.' },
  { q: 'How long does a commercial roof replacement take?', a: 'Timeline depends on building size and system type. Smaller commercial roofs of five thousand square feet or less may complete in three to five days. Large warehouses or multi-building complexes may require several weeks. We provide detailed project schedules and communicate progress daily throughout the project.' },
  { q: 'Do you offer commercial roof maintenance programs?', a: 'Yes — IWC Roofing offers preventative maintenance programs to maximize your commercial roof lifespan and prevent costly emergency repairs. Programs include biannual inspections, drain and penetration maintenance, minor repair coverage, and annual inspection reports for your property records.' },
  { q: 'What is the best flat roof system for Utah?', a: 'TPO is the most popular choice for Utah commercial buildings, offering excellent UV resistance, energy efficiency, and value. For high-traffic roofs, modified bitumen provides superior puncture resistance. EPDM excels in extreme temperature environments. Our commercial specialists help you select based on your specific building needs.' },
];

export default function CommercialRoofingPage() {
  return (
    <PageTemplate
      hero={<Hero />}
      mainSection={<MainSection />}
      aiBlock={<AIBlock />}
      processSection={<SharedProcessSection title="Our Commercial Process" subtitle="Structured for minimal business disruption and maximum quality outcomes." steps={PROCESS} />}
      faqSection={<SharedFAQSection title="Commercial Roofing Answered" faqs={FAQS} />}
      midCTA1="Utah's most trusted commercial roofing contractor — request a site assessment today."
      midCTA2="Commercial projects completed on time and on budget — get your proposal."
      midCTA3="Trusted by Utah businesses for commercial roofing since 2010."
    />
  );
}
